import React, { FC, useState } from "react";
import { DatabaseEnum } from "@Shape-Digital/kudzu-data/lib/types/common";
import Alert from "../../Unknown/Alert";
import Skeleton from "../../Unknown/Skeleton";
import Grid from "../../Unknown/Grid";
import Typography from "../../Unknown/Typography";
import Stack from "../../Unknown/Stack";
import Box from "../../Unknown/Box";
import PatientInformationCard, {
  PatientInformationCardProps,
} from "../PatientInformationCard";
import AppointmentsPreviewGrid, {
  AppointmentsPreviewGridProps,
} from "../../Appointment/AppointmentsPreviewGrid";
import useTranslations from "./useTranslations";
import PatientEditFormContainer, {
  PatientEditFormContainerProps,
} from "../PatientEditFormContainer";
import Paper from "../../Unknown/Paper";
import PatientFormsPreviewGrid, {
  PatientFormsPreviewGridProps,
} from "../PatientFormsPreviewGrid";
import Button from "../../Unknown/Button";
import { AuditLogListProps } from "../../AuditLog/AuditLogList/types";
import AuditLogCard from "../../AuditLog/AuditLogCard";
import RecordLockedState from "../../Unknown/RecordLockedState";

export type PatientMainCardsProps = {
  isEditMode: boolean;
  isLoading?: boolean;
  error?: string | null;
  data: {
    name: string;
    mergeStatus: DatabaseEnum["merge_status"] | null;
    patientInformationCardProps: PatientInformationCardProps;
    appointmentsPreviewGridPropsProps: AppointmentsPreviewGridProps;
    patientEditFormContainerProps: PatientEditFormContainerProps;
    patientFormsPreviewGridProps: PatientFormsPreviewGridProps;
    auditLogsData: AuditLogListProps;
  } | null;
};

const PatientMainCards: FC<PatientMainCardsProps> = ({
  isEditMode,
  isLoading,
  data,
  error,
}) => {
  const translations = useTranslations();
  const [isAuditLogsShown, setIsAuditLogShown] = useState(false);

  // Pillar E1 — only 'pending' takes over the whole drawer.
  if (data?.mergeStatus === "pending") {
    return <RecordLockedState />;
  }

  if (error) {
    return (
      <Box py={3} px={6}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (isLoading || !data) {
    return (
      <Stack spacing={4} py={3} px={6}>
        <Typography variant="h5">
          <Skeleton variant="rounded" />
        </Typography>
        <Skeleton height={250} variant="rounded" />
        <Skeleton height={250} variant="rounded" />
      </Stack>
    );
  }

  const {
    name,
    patientInformationCardProps,
    appointmentsPreviewGridPropsProps,
    patientEditFormContainerProps,
    patientFormsPreviewGridProps,
    auditLogsData,
  } = data;

  return (
    <Grid
      container
      spacing={3}
      py={3}
      px={6}
      direction="column"
      flexWrap="nowrap"
    >
      <Grid
        item
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="nowrap"
        p={0}
      >
        <Typography variant="h4">{name}</Typography>
        <Button onClick={() => setIsAuditLogShown((prevState) => !prevState)}>
          {isAuditLogsShown
            ? translations.hideAuditLogsBtn
            : translations.viewAuditLogsBtn}
        </Button>
      </Grid>
      {isAuditLogsShown && (
        <AuditLogCard
          title={translations.titleAuditLogs}
          auditLogsData={auditLogsData}
        />
      )}
      <Grid item xs>
        <Paper>
          <Box p={3}>
            <Typography variant="h5">
              {translations.titleInformation}
            </Typography>
            <Box mt={3}>
              {isEditMode ? (
                <PatientEditFormContainer {...patientEditFormContainerProps} />
              ) : (
                <PatientInformationCard {...patientInformationCardProps} />
              )}
            </Box>
          </Box>
        </Paper>
      </Grid>
      <Grid item xs>
        <Paper>
          <Box p={3}>
            <Typography variant="h5">
              {translations.titleAppointments}
            </Typography>
            <Box mt={3}>
              <AppointmentsPreviewGrid {...appointmentsPreviewGridPropsProps} />
            </Box>
          </Box>
        </Paper>
      </Grid>
      <Grid item xs>
        <Paper>
          <Box p={3}>
            <Typography variant="h5">{translations.titleForms}</Typography>
            <Box mt={3}>
              <PatientFormsPreviewGrid {...patientFormsPreviewGridProps} />
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default PatientMainCards;
